import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useSnackbar } from 'notistack';
import apiClient from '../../../../../../api/apiClient';
import { usePreloaderStore, useConfirmationModalStore } from '../../../../../../stores';
import { PersonFillIcon, PersonIcon, TrashIcon } from '../../../../model/icons';
import { formatDt, personName } from './challengeUtils';
import useResetChain from './useResetChain';

const CHAIN_STATUS = {
    ACTIVE: { label: 'Активна', cls: 'act' },
    COMPLETED: { label: 'Завершена', cls: 'done' },
    BROKEN: { label: 'Оборвана', cls: 'broken' }
};

const VERIFICATION_BADGE = {
    FLAGGED: { label: 'на проверке', cls: 'flag' },
    CONFIRMED: { label: 'подтверждено организатором', cls: 'ok' },
    REJECTED: { label: 'обман — награда изъята', cls: 'rej' }
};

// Цвет кольца аватара по судьбе звена (как во фронте игрока).
const ringClass = (step) => {
    if (step.status === 'EXPIRED' || step.verificationStatus === 'REJECTED') return 'red';
    if (step.status === 'IN_PROGRESS') return 'live';
    return 'ok';
};

function stepStatusText(step) {
    switch (step.status) {
        case 'IN_PROGRESS': return `выполняет · дедлайн ${formatDt(step.deadlineAt)}`;
        case 'COMPLETED': return `выполнено ${formatDt(step.completedAt)}`;
        case 'EXPIRED': return 'не успел(а)';
        default: return '';
    }
}

// По умолчанию показываем последнее звено с видео, иначе живое, иначе последнее.
function defaultStep(steps) {
    return [...steps].reverse().find(s => s.status === 'COMPLETED' && s.videoUrl)
        || steps.find(s => s.status === 'IN_PROGRESS')
        || steps[steps.length - 1];
}

function StepAvatar({ step, selected, onClick }) {
    return (
        <button
            type="button"
            className={`chn-ava ${ringClass(step)}${selected ? ' on' : ''}`}
            title={personName(step.participant)}
            onClick={onClick}
        >
            {step.participant?.avatarUrl
                ? <img src={step.participant.avatarUrl} alt="" />
                : <span className="chn-ava-ph"><PersonFillIcon /></span>}
        </button>
    );
}

StepAvatar.propTypes = {
    step: PropTypes.object.isRequired,
    selected: PropTypes.bool.isRequired,
    onClick: PropTypes.func.isRequired
};

// Карточка цепочки: лента аватаров-звеньев (клик — детали звена с видео), обрыв активной.
export default function ChainCard({ chain, number, onChanged }) {
    const { enqueueSnackbar } = useSnackbar();
    const { show, hide } = usePreloaderStore();
    const { open: openConfirm } = useConfirmationModalStore();
    const resetChain = useResetChain(onChanged);
    const status = CHAIN_STATUS[chain.status] || { label: chain.status, cls: '' };
    const steps = chain.steps || [];
    const [selectedId, setSelectedId] = useState(null);
    const selected = steps.find(s => s.id === selectedId) || defaultStep(steps);
    const verdict = selected && VERIFICATION_BADGE[selected.verificationStatus];

    // Поимённые жалобы подгружаются по клику на счётчик (в chains-DTO только reportCount).
    const [reportsByStep, setReportsByStep] = useState({});
    const [reportsOpenFor, setReportsOpenFor] = useState(null);
    const reportsOpen = selected && reportsOpenFor === selected.id;
    const toggleReports = async (stepId) => {
        if (reportsOpenFor === stepId) { setReportsOpenFor(null); return; }
        if (!reportsByStep[stepId]) {
            try {
                const res = await apiClient.get(`/api/neighbor/admin/challenges/steps/${stepId}/reports`);
                setReportsByStep(prev => ({ ...prev, [stepId]: res.data || [] }));
            } catch (e) {
                enqueueSnackbar(e.response?.data?.message || 'Не удалось загрузить жалобы', { variant: 'error' });
                return;
            }
        }
        setReportsOpenFor(stepId);
    };

    // Модерация контента: удаление видео звена без вердикта — награда и цепочка не меняются.
    const deleteVideo = (stepId) => openConfirm(
        <div className="confirm-msg">
            <b>Удалить видео звена?</b>
            <p>Видео пропадёт из сторис-фида и карточек. Вердикты, награда и цепочка не меняются. Действие необратимо.</p>
        </div>,
        async () => {
            show();
            try {
                await apiClient.delete(`/api/neighbor/admin/challenges/steps/${stepId}/video`);
                enqueueSnackbar('Видео удалено', { variant: 'success' });
                onChanged();
            } catch (e) { enqueueSnackbar(e.response?.data?.message || 'Ошибка при удалении видео', { variant: 'error' }); }
            finally { hide(); }
        },
        { confirmText: 'Удалить видео' }
    );

    return (
        <div className="chn-card">
            <div className="chn-head">
                <span className="chn-id">Цепочка #{number}</span>
                <span className={`chn-badge ${status.cls}`}>{status.label}</span>
                <span className="chn-dates">{formatDt(chain.startedAt)} → {chain.closedAt ? formatDt(chain.closedAt) : '…'}</span>
                <span className="chn-count">звеньев: {steps.length}</span>
                {chain.status === 'ACTIVE' && (
                    <button type="button" className="btn btn-ghost btn-sm" onClick={() => resetChain(chain.id)}>Оборвать</button>
                )}
            </div>

            {steps.length > 1 && (
                <div
                    className="chn-ribbon"
                    ref={(el) => {
                        // При первом рендере проматываем к концу цепочки (к живому звену)
                        if (el && !el.dataset.scrolled) {
                            el.scrollLeft = el.scrollWidth;
                            el.dataset.scrolled = '1';
                        }
                    }}
                >
                    {steps.map((s, i) => (
                        <React.Fragment key={s.id}>
                            {i > 0 && <span className="chn-arrow">→</span>}
                            <StepAvatar step={s} selected={selected?.id === s.id} onClick={() => setSelectedId(s.id)} />
                        </React.Fragment>
                    ))}
                </div>
            )}

            {selected ? (
                <div className="chn-detail">
                    <div className="chn-drow">
                        <span className={`chn-ava sm ${ringClass(selected)}`}>
                            {selected.participant?.avatarUrl
                                ? <img src={selected.participant.avatarUrl} alt="" />
                                : <span className="chn-ava-ph"><PersonFillIcon /></span>}
                        </span>
                        <div className="chn-dwho">
                            <div className="chn-name">{personName(selected.participant)}</div>
                            {selected.passedBy && <div className="chn-from">от {personName(selected.passedBy)}</div>}
                        </div>
                        <span className="chn-st">{stepStatusText(selected)}</span>
                    </div>

                    {selected.videoUrl && (
                        <div className="media-delwrap">
                            {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
                            <video className="ch-video" src={selected.videoUrl} controls preload="metadata" />
                            <button type="button" className="media-del" title="Удалить видео" onClick={() => deleteVideo(selected.id)}><TrashIcon /></button>
                        </div>
                    )}

                    {(verdict || selected.reportCount > 0) && (
                        <div className="chn-dbadges">
                            {verdict && <span className={`chn-vf ${verdict.cls}`}>{verdict.label}</span>}
                            {selected.reportCount > 0 && (
                                <button type="button" className="chn-rep" onClick={() => toggleReports(selected.id)}>
                                    жалоб: {selected.reportCount} {reportsOpen ? '▴' : '▾'}
                                </button>
                            )}
                        </div>
                    )}

                    {reportsOpen && (
                        <div className="ch-reports-list">
                            {(reportsByStep[selected.id] || []).map(r => (
                                <div className="ch-report" key={r.id}>
                                    <PersonIcon />{personName(r.reporter)}
                                    <span className="t">{formatDt(r.createdAt)}</span>
                                </div>
                            ))}
                            {(reportsByStep[selected.id] || []).length === 0 && <div className="bt-empty">Жалобы не найдены</div>}
                        </div>
                    )}
                </div>
            ) : <div className="bt-empty">Нет звеньев</div>}
        </div>
    );
}

ChainCard.propTypes = {
    chain: PropTypes.shape({
        id: PropTypes.number,
        status: PropTypes.string,
        startedAt: PropTypes.string,
        closedAt: PropTypes.string,
        steps: PropTypes.array
    }).isRequired,
    number: PropTypes.number.isRequired,
    onChanged: PropTypes.func.isRequired
};
